<template>
  <div class="constants">
    <modal class="modal-approve" :show="showConfirmReloadModal" @close="showConfirmReloadModal = false">
      <template slot="header">
        <h3>Load Data</h3>
      </template>
      <template>
        <p>You have unsaved changes. This action will reload data and erase your changes. Do you still want to proceed?</p>
      </template>
      <template slot="footer">
        <button class="btn btn-secondary" @click="showConfirmReloadModal = false">Cancel</button>
        <button class="btn btn-primary" @click="confirmReload">Proceed</button>
      </template>
    </modal>
    <div class="row">
      <div class="col col col-12 col-md-6">
        <button
          :class="{
            'btn': true,
            'btn-primary': true,
            'disabled': saveDisabled
          }"
          :disabled="saveDisabled"
          type="button"
          @click="save"
        >Save Changes</button>
        <button
          :class="{
            'btn': true,
            'btn-primary': true,
            'disabled': saveDisabled
          }"
          :disabled="saveDisabled"
          type="button"
          @click="cancel"
        >Cancel Changes</button>
      </div>
      <div class="col col-12 col-md-6 text-right">
        <button
          type="button"
          class="btn btn-secondary"
          @click="showFilters = !showFilters"
        >
          <span>Filters</span>
          <i :class="{
            'fas': true,
            'fa-sort-up': showFilters,
            'fa-sort-down': !showFilters
          }"/>
        </button>
        <button
          type="button"
          class="btn btn-secondary"
          @click="showColumns = !showColumns"
        >
          <span>Columns</span>
          <i :class="{
            'fas': true,
            'fa-sort-up': showColumns,
            'fa-sort-down': !showColumns
          }"/>
        </button>
      </div>
    </div>
    <slide-y-up-transition origin="center top">
      <div class="row filters" v-if="showFilters">
        <div class="col col-12">
          <input
            type="search"
            v-model="filter"
            @input="debouncedLoad"
          />
          <button
            class="btn btn-secondary"
            type="button"
            @click="filter = ''"
          >
            <span>Clear</span>
            <i class="fas fa-times"/>
          </button>
        </div>
      </div>
    </slide-y-up-transition>
    <slide-y-up-transition origin="center top">
      <aside
        v-if="showColumns"
        class="columns-popover"
      >
        <ul>
          <li v-for="column in columns" :key="column.name">
            <input
              :id="`column-hidden-${column.slug}`"
              type="checkbox"
              v-model="column.hidden"
              :true-value="false"
              :false-value="true"
            />
            <label
              :for="`column-hidden=${column.slug}`"
            >{{ column.name }}</label>
          </li>
        </ul>
      </aside>
    </slide-y-up-transition>
    <div class="row my-2">
      <div class="col col-12 col-md-6">
        <button
          :class="{
            'btn': true,
            'btn-secondary': true,
            'disabled': loading
          }"
          :disabled="loading"
          type="button"
          @click="addRow"
        >
          <span>Add Row</span>
          <i class="fas fa-plus"/>
        </button>
      </div>
      <div class="col col-12 col-md-6 text-right">
        <file-download-link
          extraClasses="d-md-inline-block mr-md-1"
          src="/api/constants/excel"
        />
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="table-wrapper">
          <div class="table-responsive">
            <table
              :class="{
                'table': true 
              }"
            >
              <thead>
                <tr>
                  <th><!-- Control Column --></th>
                  <th
                    v-for="column in visibleColumns"
                    :key="column.name"
                    @click="toggleSort(column.name)"
                  >
                    <span class="column-name">{{ column.title }}</span>
                    <i :class="{
                      'column-sort': true,
                      'fa': true,
                      'fa-sort': column.sort === 'None',
                      'fa-sort-up': column.sort === 'Ascending',
                      'fa-sort-down': column.sort === 'Descending'
                    }"/>
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-if="rows.length">
                  <tr
                    v-for="(row, rowIndex) in rows" 
                    :key="row.ID"
                    :class="{
                      'deleted': row.deleted,
                      'dirty': rowDirty(row),
                      'new': row.new
                    }"
                  >
                    <th scope="row">
                      <button
                        v-if="row.deleted"
                        class="btn btn-secondary row-control row-restore"
                        title="Restore row."
                        @click="restoreRow(row)"
                      >
                        <i class="fas fa-undo" />
                      </button>
                      <button
                        v-else
                        class="btn btn-secondary row-control row-delete"
                        title="Delete row."
                        @click="deleteRow(row)"
                      >
                        <i class="fas fa-times" />
                      </button>
                      <button
                        class="btn btn-secondary row-control row-delete"
                        title="Duplicate row."
                        @click="duplicateRow(row)"
                      >
                        <i class="fas fa-copy" />
                      </button>
                    </th>
                    <td 
                      v-for="(column, columnIndex) in visibleColumns" 
                      :key="column.name"
                      :class="{
                        'dirty': columnDirty(row, column.name)
                      }"
                    >
                      <select
                        v-if="column.type === 'select'"
                        v-model="row[column.name]"
                        :required="column.required"
                        :readonly="column.readOnly"
                        :data-row-index="rowIndex"
                        :data-column-index="columnIndex"
                        ref="input"
                        @keydown.up="inputKeyUp"
                        @keydown.down="inputKeyDown"
                      >
                        <option
                          v-for="option in column.options"
                          :key="option"
                          :value="option"
                        >{{ option }}</option>
                      </select>
                      <input
                        v-else-if="column.name === 'ID'"
                        :type="column.type"
                        :required="column.required"
                        :readonly="column.readOnly"
                        :value="row['ID'].toString().match(/^new-/) ? '' : row.ID"
                        :data-row-index="rowIndex"
                        :data-column-index="columnIndex"
                        ref="input"
                        @keydown.up="inputKeyUp"
                        @keydown.down="inputKeyDown"
                      />
                      <input
                        v-else
                        v-model="row[column.name]"
                        :type="column.type"
                        :required="column.required"
                        :readonly="column.readOnly"
                        :data-row-index="rowIndex"
                        :data-column-index="columnIndex"
                        ref="input"
                        @keydown.up="inputKeyUp"
                        @keydown.down="inputKeyDown"
                      />
                    </td>
                  </tr>
                </template>
                <template v-else-if="loading">
                  <tr>
                    <td :colspan="columns.length + 1">Loading...</td>
                  </tr>
                </template>
                <template v-else>
                  <tr>
                    <td :colspan="columns.length + 1">No data found.</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
          <aside v-if="loading" class="loading">
            <p>Loading...</p>
          </aside>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col col-12">
        <nav class="pager mt-2" title="Pager">
          <button
            :class="{
              'btn': true,
              'btn-secondary': true,
              'disabled': previousPageDisabled
            }"
            :disabled="previousPageDisabled"
            type="button"
            @click="setPage(1)"
          >
            <i class="fas fa-step-backward" />
            <span>First</span>
          </button>
          <button
            :class="{
              'btn': true,
              'btn-secondary': true,
              'disabled': previousPageDisabled
            }"
            :disabled="previousPageDisabled"
            type="button"
            @click="setPage(page - 1)"
          >
            <i class="fas fa-caret-left" />
            <span>Previous</span>
          </button>
          <label for="pager-page">Page</label>
          <input
            id="pager-page"
            type="number"
            :class="{
              'disabled': loading,
              'mx-1': true
            }"
            :disabled="loading"
            min="1"
            :max="lastPage"
            step="1"
            v-model="page"
          />
          <span>of {{ lastPage }}</span>
          <input
            id="pager-page-size"
            type="number"
            :class="{
              'disabled': loading,
              'mx-1': true
            }"
            :disabled="loading"
            min="1"
            :max="maxPageSize"
            step="1"
            v-model="pageSize"
          />
          <label for="pager-page-size">Per Page</label>
          <button
            :class="{
              'btn': true,
              'btn-secondary': true,
              'disabled': nextPageDisabled,
              'ml-1': true,
            }"
            :disabled="nextPageDisabled"
            type="button"
            @click="setPage(page + 1)"
          >
            <span>Next</span>
            <i class="fas fa-caret-right" />
          </button>
          <button
            :class="{
              'btn': true,
              'btn-secondary': true,
              'disabled': nextPageDisabled
            }"
            :disabled="nextPageDisabled"
            type="button"
            @click="setPage(lastPage)"
          >
            <span>Last</span>
            <i class="fas fa-step-forward" />
          </button>
        </nav>
      </div>
    </div>
  </div>
</template>
<script>
import FileDownloadLink from '../../components/FileDownloadLink.vue';

export default {
  name: 'Constants',
  components: {
    FileDownloadLink
  },
  data() {
    const columnNames = [
      'ID',
      'ConstantID',
      'ConstantName',
      'Description',
      'C_Value',
      'UM',
      'LookupCategory',
      'Cond_Sign',
      'Parameter',
      'Source',
      'AddedOn',
      'AddedBy',
      'LastUpdatedOn',
      'LastUpdatedBy',
      'DeletedAt',
    ];

    const columnTitles = {
      'ID': 'ID',
      'ConstantID': 'Constant ID',
      'LookupCategory': 'Lookup Category',
      'ConstantName': 'Constant Name',
      'Description': 'Description',
      'Cond_Sign': 'Condition Sign',
      'Parameter': 'Parameter',
      'C_Value': 'Constant Value',
      'UM': 'Unit of Measure',
      'Source': 'Source',
      'AddedOn': 'Added On',
      'AddedBy': 'Added By',
      'LastUpdatedOn': 'Last Updated On',
      'LastUpdatedBy': 'Last Updated By',
      'DeletedAt': 'Deleted At',
    };

    const hiddenColumns = [
      'AddedOn',
      'AddedBy',
      'LastUpdatedOn',
      'LastUpdatedBy',
      'DeletedAt',
    ];

    const readOnlyColumns = [
      'ID',
      'AddedOn',
      'AddedBy',
      'LastUpdatedOn',
      'LastUpdatedBy',
      'DeletedAt',
    ];

    const requiredColumns = [
      'ConstantID',
      'C_Value',
    ];

    const selectOptions = {
      'Cond_Sign': [
        'NA',
        '=',
        '>',
        '<',
        '>=',
        '<=',
      ]
    };

    const columns = [];

    for(let i = 0; i < columnNames.length; i++) {
      const columnName = columnNames[i];

      columns.push({
        name: columnName,
        title: columnTitles[columnName],
        slug: columnName.toLowerCase().replace('/_/', '-'),
        hidden: hiddenColumns.includes(columnName),
        readOnly: readOnlyColumns.includes(columnName),
        type: selectOptions[columnName] ? 'select' : 'input',
        options: selectOptions[columnName] || null,
        required: requiredColumns.includes(columnName),
        sort: 'None'
      });
    }

    return {
      columns,
      defaultPage: 1,
      defaultPageSize: 25,
      filter: '',
      lastPage: 1,
      loading: false,
      loadSource: null,
      loadTimeout: null,
      page: 1,
      pageSize: 25,
      maxPageSize: 100,
      newID: 1,
      reloadConfirmed: false,
      rows: [],
      showColumns: false,
      showConfirmReloadModal: false,
      showFilters: false,
      totalRecords: 0,
    };
  },
  computed: {
    hasUnsavedChanges() {
      return this.rows && this.rows.length && this.rows.find(row => this.rowDirty(row) || row.new || row.deleted);
    },
    previousPageDisabled() {
      return this.loading || this.page === 1;
    },
    nextPageDisabled() {
      return this.loading || this.page >= this.lastPage;
    },
    saveDisabled() {
      return this.loading || !this.hasUnsavedChanges;
    },
    visibleColumns() {
      return this.columns.filter(column => !column.hidden);
    }
  },
  methods: {
    addRow() {
      const row = {
        deleted: false,
        original: {},
        new: true,
      };

      for(let i = 0; i < this.columns.length; i++) {
        const columnName = this.columns[i].name;

        row[columnName] = '';
        row.original[columnName] = '';
      }

      const newRowID = this.newID++;

      row['ID'] = 'new-' + newRowID;
      row['ConstantID'] = '[C' + (this.totalRecords + newRowID) + ']';

      this.rows.unshift(row);
    },
    cancel() {
      this.rows = this.rows.filter(row => !row.new);

      for(let i = 0; i < this.rows.length; i++) {
        const row = this.rows[i];

        if(row.deleted) {
          row.deleted = false;
        }

        if(this.rowDirty(row)) {
          for(let field in row.original) {
            if(row[field] != row.original[field]) {
              row[field] = row.original[field];
            }
          }
        }
      }
    },
    columnByName(columnName) {
      return this.columns.find(column => column.name === columnName);
    },
    columnDirty(row, columnName) {
      return row.original[columnName] != row[columnName];
    },
    confirmReload() {
      this.reloadConfirmed = true;
      this.showConfirmReloadModal = false;
    },
    debouncedLoad() {
      if(this.loadTimeout) {
        clearTimeout(this.loadTimeout)
      }

      this.loadTimeout = setTimeout(this.load.bind(this), 500);
    },
    deleteRow(row) {
      if(row.new) {
        const rowIndex = this.rows.findIndex(findRow => findRow.ID == row.ID);
        
        this.rows.splice(rowIndex, 1);
      } else {
        row.deleted = true;
      }
    },
    duplicateRow(row) {
      this.addRow();

      const newRow = this.rows[0];

      for(let i = 0; i < this.columns.length; i++) {
        const columnName = this.columns[i].name;

        if(columnName !== 'ID') {
          newRow[columnName] = row[columnName];
        }
      }
    },
    inputKeyCycle(event, rowOffset, columnOffset) {
      const rowIndex = parseInt(event.target.getAttribute('data-row-index'));
      const columnIndex = parseInt(event.target.getAttribute('data-column-index'));
      
      const targetRowIndex = (rowIndex + rowOffset) % this.rows.length;
      const targetColumnIndex = (columnIndex + columnOffset) % this.columns.length;
      
      const targetElement = this.$refs.input.find(el => {
        return el.getAttribute('data-row-index') == targetRowIndex 
        && el.getAttribute('data-column-index') == targetColumnIndex
      });

      targetElement.focus();
    },
    inputKeyDown(event) {
      this.inputKeyCycle(event, 1);
    },
    inputKeyUp(event) {
      this.inputKeyCycle(event, -1);
    },
    load() {
      if(this.loadTimeout) {
        clearTimeout(this.loadTimeout);
        this.loadTimeout = null;
      }

      if(this.hasUnsavedChanges && !this.reloadConfirmed) {
        this.showConfirmReloadModal = true;
        
        return;
      }

      const params = {};

      if(this.page !== this.defaultPage) {
        params['page'] = this.page;
      }

      if(this.pageSize !== this.defaultPageSize) {
        params['page-size'] = this.pageSize;
      }

      if(this.filter !== '') {
        params['filter'] = this.filter;
      }

      for(let i = 0; i < this.columns.length; i++) {
        const column = this.columns[i];

        if(column.sort !== 'None') {
          params['sort-' + column.slug] = column.sort === 'Ascending' ? 'ASC' : 'DESC';
        }
      }

      const searchParams = new URLSearchParams(params);
      const pageURL = window.location.pathname + Object.keys(params).length ? '?' + searchParams : '';

      history.pushState({}, null, pageURL)

      const apiURL = '/api/constants?' + searchParams;

      if(this.loading && this.loadSource) {
        this.loadSource.cancel()
      }

      this.loadSource = this.$http.CancelToken.source()
      this.loading = true;

      return this.$http
        .get(apiURL, {
          cancelToken: this.loadSource.token
        })
        .then((response) => {
          if(!response) {
            throw "No response.";
          }

          if(!response.data) {
            throw "No response data.";
          }

          const rows = response.data.data;

          for(let i = 0; i < rows.length; i++) {
            const row = rows[i];

            row.original = JSON.parse(JSON.stringify(row));
            row.deleted = false;
            row.new = false;
          }

          this.lastPage = parseInt(response.data.last_page)
          this.pageSize = parseInt(response.data.per_page)
          this.totalRecords = parseInt(response.data.total)

          this.rows = rows;
          this.reloadConfirmed = false;
        })
        .catch((error) => {
          if(!this.$http.isCancel(error)) {
            console.error(error);
            alert('There was a problem loading the data. Please try again.');
          }
        })
        .finally(() => {
          this.loading = false;
        })
    },
    restoreRow(row) {
      row.deleted = false;
    },
    rowByID(rowID) {
      return this.rows.find(row => row.ID === rowID);
    },
    rowDirty(row) {
      for(let i = 0; i < this.columns.length; i++) {
        const columnName = this.columns[i].name;
        const currentValue = (row[columnName] || '').toString();
        const originalValue = (row.original[columnName] || '').toString();

        if(currentValue !== originalValue) {
          return true;
        }
      }

      return false;
    },
    save() {
      const insertions = [];
      const deletions = [];
      const updates = [];

      for(let i = 0; i < this.rows.length; i++) {
        const row = this.rows[i];

        if(row.deleted) {
          deletions.push(row.ID);
        } else if(row.new) {
          const insertion = {};

          for(let j = 0; j < this.columns.length; j++) {
            const columnName = this.columns[j].name;

            if(columnName !== 'ID') {
              insertion[columnName] = row[columnName];
            }
          }

          insertions.push(insertion);
        } else if(this.rowDirty(row)) {
          const update = {};

          for(let field in row.original) {
            if(row[field] != row.original[field]) {
              update[field] = row[field];
            }
          }

          update['ID'] = row.ID;
          updates.push(update);
        }
      }

      if(!deletions.length && !insertions.length && !updates.length) {
        return;
      }

      this.loading = true;

      this.$http
        .post('/api/constants', {
          delete: deletions,
          insert: insertions,
          update: updates
        })
        .then(() => {
          this.loading = false;
          this.reloadConfirmed = true;
          this.load();
        })
        .catch(error => {
          console.log(error);
          alert('There was a problem saving the data. Please try again.');
        })
    },
    setPage(pageNumber) {
      if(pageNumber < 1) {
        pageNumber = 1;
      } else if(pageNumber > this.lastPage) {
        pageNumber = this.lastPage;
      }

      if(this.page !== pageNumber) {
        this.page = pageNumber;
        this.debouncedLoad();
      }
    },
    toggleSort(columnName) {
      if(this.hasUnsavedChanges && !this.reloadConfirmed) {
        this.showConfirmReloadModal = true;

        return;
      }

      const column = this.columnByName(columnName);

      switch(column.sort) {
        case 'Ascending': column.sort = 'Descending'; break;
        case 'Descending': column.sort = 'None'; break;
        default: column.sort = 'Ascending'; break;
      }

      this.load();
    },
  },
  mounted() {
    this.load();
  }
}
</script>
<style lang="scss">
  .constants {
    .column-sort {
      margin-left: 8px;
      cursor: pointer;
    }

    .columns-popover {
      position: absolute;
      top: 48px;
      right: 16px;
      padding: 12px;
      border-radius: 4px;
      z-index: 1;
      background-color: white;
      box-shadow: 2px 2px 8px rgba(0, 0, 0, .5);

      input {
        cursor: pointer;
      }

      label {
        display: inline-block;
        position: relative;
        top: -2px;
        margin-left: 4px;
        font-weight: bold;
        cursor: pointer;
      }
    }

    .filters {
      .col {
        display: flex;
        margin-top: 6px;

        input {
          display: inline-block;
          flex-grow: 1;
          position: relative;
          height: 100%;
          margin-right: 4px;
          padding: 8px;
          border: 1px lightgray solid;
          border-radius: 4px;
        }
      }
    }

    .row-control {
      width: 20px;
      height: 20px;
      margin-right: 2px !important;
      margin-top: 6px;
      padding: 0;
      font-size: 8pt;

      &:first-child {
        margin-left: 4px;
      }
    }

    .table-wrapper {
      .loading {
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, .5);
        color: white;
        font-size: 18pt;
        text-align: center;
        text-shadow: 2px 2px 2px rgba(0, 0, 0, .5);
        text-transform: uppercase;
      }

      .table {
        thead {
          th {
            position: relative;
            padding-right: 16px;
            vertical-align: middle;
            white-space: normal;

            i {
              position: absolute;
              top: calc(50% - 6px);
              right: 0;
            }
          }
        }

        tbody {
          & th,
          & td {
            padding: 0;
          }

          & td input,
          & td select {
            display: block;
            width: 100%;
            height: 100%;
            padding: 8px;
            border: none;
            background-color: transparent;
          }

          tr {
            &:nth-child(odd) {
              background-color: #eee;
            }

            &.dirty {
              td.dirty {
                background-color: #e36c6c;
              }

              & td,
              & th {
                background-color: #efb1b1;
              }

              &:nth-child(even) {
                td.dirty {
                  background-color: #ffa6a6;
                }

                & td,
                & th {
                  background-color: #f5e0e0;
                }
              }
            }

            &.new {
              & td,
              & th,
              & td.dirty {
                background-color: #bfe3bf;
              }

              &:nth-child(even) {
                & td,
                & th,
                & td.dirty {
                  background-color: #f1fbf1;  
                }
              }
            }

            &.deleted {
              td {
                input {
                  text-decoration: line-through;
                }
              }
            }
          }
        }
      }
    }
  }
</style>